import { RefObject, useCallback, useLayoutEffect, useRef } from "react";
import type { MapRef } from "react-map-gl/maplibre";
import {
  hasUsableDimensions,
  hasValidCamera,
} from "../../../utils/maps/MapHelperFunctions";

export const useMapResize = (
  containerRef: RefObject<HTMLElement>,
  mapRef: RefObject<MapRef>
) => {
  const animationFrameRef = useRef<number>();

  const resizeMap = useCallback(() => {
    if (animationFrameRef.current !== undefined) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      animationFrameRef.current = undefined;
      const map = mapRef.current;
      const mapContainer = map?.getContainer();

      if (
        !map ||
        !mapContainer ||
        !hasUsableDimensions(mapContainer) ||
        !hasValidCamera(map)
      ) {
        return;
      }

      map.resize();
    });
  }, [mapRef]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const handleContainerResize = () => {
      const isReady = hasUsableDimensions(container);
      if (isReady) {
        resizeMap();
      }
    };

    handleContainerResize();

    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(handleContainerResize);
    observer?.observe(container);

    // Native fullscreen changes the MapLibre child rather than this wrapper,
    // so it needs an explicit resize in addition to wrapper observation.
    document.addEventListener("fullscreenchange", handleContainerResize);

    return () => {
      observer?.disconnect();
      document.removeEventListener("fullscreenchange", handleContainerResize);
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [containerRef, resizeMap]);

  return resizeMap;
};
